<script setup lang="ts">
import {reactive, ref} from "vue";
import {ElMessageBox, ElNotification} from "element-plus";
import CourseCreatorDialog from "components/dialog/CourseCreatorDialog.vue";
import Course from "views/creator/course/CourseList.vue";
import {CourseService, SystemService} from "@/api/api.ts";
import {CourseCate, CourseList, CourseSubCate} from "@/types";
import {Search} from "@element-plus/icons-vue";

const courseCreatorDialog = ref<InstanceType<typeof CourseCreatorDialog>>()
const loading = ref(false)
const status = ref<number>(-1)
const is_secret = ref<number>()
const courseId = ref<number>()
const cateList = ref<CourseCate[]>([])
const twoCateList = ref<CourseSubCate[]>()
const cateId = ref<number>()
const twoCateId = ref<number>()
const content = ref('')
const courseList = ref<CourseList>()
const pagination = reactive({
  current: 1,
  pageSize: 5,
  total: 0,
})

const initCate = async () => {
  try {
    const res = await SystemService.getCourseCateList({})
    if (res.data.code == 200) {
      cateList.value = res.data.data || [];
      cateList.value.unshift({id: 0, name: "全部"})
    }
  } catch (err) {
    console.warn(err)
  }
}

initCate()

const onCateChange = (cateId: any) => {
  for (let i = 0; i < cateList.value.length; i++) {
    if (cateList.value[i].id === cateId) {
      twoCateId.value = undefined
      twoCateList.value = cateList.value[i].two_list || []
      if (!twoCateList.value.some(item => item.id === 0)) {
        twoCateList.value?.unshift({
          id: 0,
          pid: cateId,
          name: "全部",
        })
      }
      onSearch()
      break
    }
  }
}

const onSearch = () => {
  pagination.current = 1
  getCourseList()
}

const onTabChange = () => {
  onSearch()
}

const getCourseList = async () => {
  try {
    loading.value = true
    const res = await CourseService.getCourseList({
      page: pagination.current,
      pageSize: pagination.pageSize,
      type: 4,
      status: status.value === -1 ? undefined : status.value,
      id: courseId.value,
      cate_id: cateId.value,
      two_cate_id: twoCateId.value,
      content: content.value,
      is_secret: is_secret.value === -1 ? undefined : is_secret.value,
    })
    if (res.data.code === 200) {
      courseList.value = res.data.data
    } else {
      ElNotification.error({
        title: '获取课程列表失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (err) {
    console.warn(err)
  } finally {
    loading.value = false
  }
}

const onCurrentChange = (page: number) => {
  pagination.current = page
  getCourseList()
}

const onPageSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  getCourseList()
}

const onDelete = (id: number) => {
  deleteCourse(id)
}

const deleteCourse = async (id: number) => {
  try {
    await ElMessageBox.confirm(
        '确认删除吗',
        '注意',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
    )
    const res = await CourseService.delCourse({
      id: id,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: '删除课程成功',
        message: res.data.msg,
      })
      onSearch()
    } else {
      ElNotification.error({
        title: '删除课程失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (err) {
    console.warn(err)
  }
}

const onCreateCourse = () => {
  courseCreatorDialog.value?.show(4)
}

getCourseList()
</script>

<template>
  <div class="mx-3rem mt-2.25rem flex items-center gap-1.25rem relative">
    <div class="w-.3125rem h-1.5rem bg-#ff6600cc rounded-md"></div>
    <div class="text-1.25em text-[rgba(0,0,0,0.851)]">组合课管理</div>
    <el-button class="absolute right-0" type="primary" @click="onCreateCourse">创建课程</el-button>
  </div>
  <div class="bg-white mx-3rem rounded-md py-1rem px-2rem min-h-50% mt-1.5rem shadow-md" v-loading="loading">
    <el-tabs class="tabs" v-model="status" @tab-change="onTabChange">
      <el-tab-pane :label="`全部 ` + `${courseList?.course_count.count || 0}`" :name="-1"></el-tab-pane>
      <el-tab-pane :label="`待发布 ` + `${courseList?.course_count.note_issue_count || 0}`" :name="2"></el-tab-pane>
      <el-tab-pane :label="`已上架 ` + `${courseList?.course_count.sale || 0}`" :name="1"></el-tab-pane>
      <el-tab-pane :label="`已下架 ` + `${courseList?.course_count.not_sale || 0}`" :name="3"></el-tab-pane>
    </el-tabs>
    <div class="flex items-center gap-2rem flex-wrap">
      <div>
        <el-select v-model="is_secret" style="width: 10rem" placeholder="课程定位" @change="onSearch">
          <el-option label="课程定位" :value="-1"></el-option>
          <el-option label="公开" :value="0"></el-option>
          <el-option label="私密" :value="1"></el-option>
          <el-option label="联培" :value="2"></el-option>
        </el-select>
      </div>
      <div>
        <el-select v-model="cateId" placeholder="选择分类" style="width: 10rem" @change="onCateChange">
          <el-option v-for="item in cateList" :label="item.name" :value="item.id"></el-option>
        </el-select>
      </div>
      <div>
        <el-select v-model="twoCateId" placeholder="选择二级分类" style="width: 10rem" @change="onSearch">
          <el-option v-for="item in twoCateList" :label="item.name" :value="item.id"></el-option>
        </el-select>
      </div>
      <div>
        <el-input v-model="courseId" :suffix-icon="Search" placeholder="搜索课程ID" @change="onSearch" clearable></el-input>
      </div>
      <div>
        <el-input v-model="content" :suffix-icon="Search" placeholder="搜索课程标题" @change="onSearch" clearable></el-input>
      </div>
    </div>
    <course :pagination="pagination" :course-list="courseList"
            @on-current-change="onCurrentChange"
            @on-page-size-change="onPageSizeChange"
            @on-delete="onDelete"/>
    <el-empty class="mt-2rem" v-if="!courseList || courseList.list.length === 0" description="没有找到课程~"/>
  </div>
  <course-creator-dialog ref="courseCreatorDialog"></course-creator-dialog>
</template>

<style scoped>
.tabs {
  --el-font-size-base: 16px;
}
</style>
